<template>
  <div class="files-upload-form">
    <div class="files-upload-form--title">{{ $t('Account.UploadCenter.Form.Title') }}</div>
    <div class="files-upload-form--filetypes">{{ $t('Account.UploadCenter.Form.AcceptedFileTypes') }}</div>
    <form enctype="multipart/form-data" @submit.prevent="uploadFiles">
      <div v-for="(_, index) in numberOfFiles" :key="index" class="upload-item">
        <input ref="fileInputs" type="file" class="file-input-hidden" @change="onFileChange($event, index)" :disabled="successInputs[index]" />
        <div class="file-input-visual" :class="{ disabled: successInputs[index], 'upload-error': errorInputs[index] }" @click="openFilePicker(index)">
          <span class="file-input-text">
            {{ getFileInputText(index) }}
          </span>
          <span class="file-input-icon">
            <svg v-if="errorInputs[index] && !successInputs[index]" class="file-input-icon--error" width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="11.5" cy="11.5" r="10" stroke="#FF4848" stroke-width="3"/>
              <line x1="8.12132" y1="8" x2="15" y2="14.8787" stroke="#FF4848" stroke-width="3" stroke-linecap="round"/>
              <line x1="15" y1="8.12132" x2="8.12132" y2="15" stroke="#FF4848" stroke-width="3" stroke-linecap="round"/>
            </svg>
            <svg v-if="!successInputs[index]" class="file-input-icon--upload" width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="1.5" y1="16.4091" x2="17.5" y2="16.4091" stroke="#4A597E" stroke-width="3" stroke-linecap="round"/>
              <line x1="1.5" y1="10.7727" x2="1.5" y2="16.4091" stroke="#4A597E" stroke-width="3" stroke-linecap="round"/>
              <line x1="17.9091" y1="10.7727" x2="17.9091" y2="16.4091" stroke="#4A597E" stroke-width="3" stroke-linecap="round"/>
              <path d="M9.5 12.7273L9.5 1.50002" stroke="#4A597E" stroke-width="3" stroke-miterlimit="11.4737" stroke-linecap="round"/>
              <path d="M14.6818 6.68188L9.49994 1.50007" stroke="#4A597E" stroke-width="3" stroke-miterlimit="11.4737" stroke-linecap="round"/>
              <path d="M4.31824 6.68188L9.50006 1.50007" stroke="#4A597E" stroke-width="3" stroke-miterlimit="11.4737" stroke-linecap="round"/>
            </svg>
            <svg v-if="!errorInputs[index] && successInputs[index]" class="file-input-icon--success" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11 0C4.92303 0 0 4.92303 0 11C0 17.077 4.92303 22 11 22C17.077 22 22 17.077 22 11C22 4.92303 17.0736 0 11 0ZM11 19.9379C6.06331 19.9379 2.06207 15.9367 2.06207 11C2.06207 6.06331 6.06331 2.05866 11 2.05866C15.9367 2.05866 19.9379 6.0599 19.9379 10.9966C19.9379 15.9333 15.9367 19.9345 11 19.9345V19.9379Z" fill="#00D828"/>
              <path d="M8.80401 17.3305L4.41273 12.8786C3.86226 12.3206 3.86226 11.4169 4.41273 10.862C4.96321 10.3039 5.8546 10.3039 6.40194 10.862L8.80401 13.2972L14.9311 7.08554C15.4816 6.52748 16.373 6.52748 16.9204 7.08554C17.4708 7.64361 17.4708 8.54729 16.9204 9.10219L8.80401 17.3337V17.3305Z" fill="#00D828"/>
            </svg>
          </span>
        </div>
      </div>
      <p>{{ $t('Account.UploadCenter.Form.Notice') }}</p>
      <div style="text-align: center;">
        <v-btn type="submit" :disabled="isSubmitDisabled" color="success" x-large>{{ $t('Account.UploadCenter.Form.Submit') }}</v-btn>
      </div>
    </form>
  </div>
</template>

<script>
import lib from '@/library'

export default {
  name: 'FilesUploadForm',
  props: [
    'numberOfFiles'
  ],
  data () {
    return {
      files: [],
      successInputs: [],
      errorInputs: [],
      isUploading: false
    }
  },
  computed: {
    isSubmitDisabled () {
      return (this.isUploading) || (this.files.length === 0) || (this.successInputs.every(Boolean))
    },
    allFilesUploaded () {
      if (!this.numberOfFiles) return false
      for (let i = 0; i < this.numberOfFiles; i++) {
        if (!this.files[i] || this.successInputs[i] !== true) {
          return false
        }
      }
      return true
    }
  },
  methods: {
    getFileInputText (index) {
      if (this.files[index] && !this.errorInputs[index]) {
        return this.files[index].name
      } else if (this.files[index] && !this.successInputs[index] && this.errorInputs[index]) {
        return 'Upload failed. Please try again.'
      } else {
        return 'SELECT FILE'
      }
    },
    openFilePicker (index) {
      if (this.successInputs[index]) return
      this.$refs.fileInputs[index].click()
    },
    onFileChange (event, index) {
      const file = event.target.files[0] || null
      this.$set(this.files, index, file)
      this.$set(this.successInputs, index, false)
      this.$set(this.errorInputs, index, false)
    },
    async uploadFiles () {
      const formData = new FormData()
      this.files.forEach((file, index) => {
        if (this.successInputs[index]) return
        if (!file) return
        formData.append(`file_${index + 1}`, file)
      })
      try {
        this.isUploading = true
        await lib.rpcService.callBroker('iw', 'uploadcustomdocuments', formData)
        this.$emit('files-upload-successful', true)
        this.files.forEach((file, index) => {
          if (file) {
            this.$set(this.successInputs, index, true)
            this.$set(this.errorInputs, index, false)
          }
        })
      } catch (error) {
        console.log(`An error has occured: ${error}`)
        this.files.forEach((file, index) => {
          if (!file || this.successInputs[index]) return
          this.$set(this.errorInputs, index, true)
        })
      } finally {
        this.isUploading = false
      }
    }
  },
  watch: {
    allFilesUploaded (value) {
      if (value) {
        this.$emit('all-files-uploaded', true)
      }
    }
  }
}
</script>

<style scoped>
.files-upload-form--title {
  background: var(--account-pages-table-headers-bgr);
  background-color: var(--account-pages-table-headers-bgr);
  color: var(--account-pages-table-headers-color);
  border-radius: var(--account-pages-table-headers-radius);
  text-align: left !important;
  font-size: 20px;
  font-weight: 400 !important;
}
.files-upload-form form,
.files-upload-form--filetypes,
.files-upload-form--title {
  padding: 10px;
}
.files-upload-form form > p {
  margin-top: 20px;
}
.file-input-hidden {
  display: none;
}
.file-input-visual {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  padding: 0 12px;
  border: 1px solid rgba(139, 139, 139, 1);
  border-radius: 4px;
  background-color: rgba(245, 245, 245, 1);
  cursor: pointer;
  font-size: 14px;
  margin-bottom: 10px;
}
.file-input-visual.upload-error {
  background-color: #fdecea;
  border: none;
}
.file-input-visual.disabled {
  background-color: #edf7ed;
  opacity: 0.8;
  border: none;
  cursor: not-allowed;
}
.file-input-text {
  color: var(--account-pages-infomsg-default-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.file-input-icon {
  font-size: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.file-input-icon--error {
  margin-right: 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/account/UploadCenter/FilesUploadForm.vue